import type { DictFragment } from '../translations';

/** Proposals showcase page ("Proposition nouvelle vue jalons/tâches") and its proposals. */
export const proposals: DictFragment = {
  fr: {
    'page.proposals': 'Proposition nouvelle vue jalons/tâches',
    'shell.nav.proposals': 'Proposition nouvelle vue jalons/tâches',
    'proposals.intro': 'Propositions de modifications de la vue jalons/tâches, soumises au PMO. Visible uniquement par les personnes autorisées.',
    'proposals.emptyTitle': 'Aucune proposition pour le moment',
    'proposals.emptyDesc': 'Les propositions ajoutées apparaîtront ici, chacune dans son onglet.',

    // [Proposition: roles-projet] — names and explanations of the four project roles (edit here to redefine them)
    'roles.accompanist': 'Accompagnant·e PMO',
    'roles.accompanist.desc': 'Pilote le projet au quotidien avec l’équipe : organise les jalons, répartit les tâches, suit l’avancement. Choisi·e parmi les admins (le PMO), qui seuls valident les changements.',
    'roles.sponsor': 'Sponsor',
    'roles.sponsor.desc': 'Porte le projet et en répond. Arbitre, valide les grandes étapes et le passage en Handover.',
    'roles.contributor': 'Contributeur·rice',
    'roles.contributor.desc': 'Réalise des tâches du projet et en rend compte.',
    'roles.consulted': 'Consulté·e',
    'roles.consulted.desc': 'Sollicité·e ponctuellement pour son avis ou son expertise sur une tâche précise, sans en être responsable.',
    'roles.sponsorFromField': 'Le sponsor et l’accompagnant·e PMO se définissent uniquement dans leurs champs « Sponsor » et « Accompagnant PMO » de la fiche projet.',
    'roles.none': 'À définir',
    'roles.none.desc': 'Aucun rôle choisi pour cette personne.',
    'roles.header': 'Rôle',
    'roles.precision': 'Précision',
    'roles.precisionPlaceholder': 'Précision (ex. négociation fournisseurs)',
    'roles.title': 'Rôles du projet',
    'roles.teamTitle': 'Équipe du projet et rôles',
    'roles.teamOpen': 'Équipe et rôles',
    'roles.teamOpenHelp': 'Voir qui fait quoi dans le projet : chaque personne, son rôle et la précision de son rôle',
    'roles.teamEmpty': 'Aucune personne n’est encore inscrite dans l’équipe de ce projet (onglet Cadrage).',
    'roles.standardDesc': 'Rôle standard',
    'roles.projectPrecision': 'Précision pour ce projet',
    'roles.definitionsTitle': 'Les 4 rôles',
    'roles.stagesTitle': 'Les rôles aux étapes du projet',
    'roles.stage.cadrage.label': 'Cadrage',
    'roles.stage.cadrage': 'On compose l’équipe : chaque personne reçoit un rôle et, si besoin, une précision. Le Sponsor est la personne indiquée dans le champ « Sponsor » de la fiche projet. Chaque personne ajoutée est prévenue par e-mail et retrouve ce rôle dans Gérance → Implications.',
    'roles.stage.planning.label': 'Planification',
    'roles.stage.planning': 'L’accompagnant·e PMO organise les jalons et répartit les tâches entre les membres de l’équipe.',
    'roles.stage.execution.label': 'Exécution',
    'roles.stage.execution': 'Les Contributeur·rice·s réalisent les tâches ; les Consulté·e·s sont sollicité·e·s au besoin. Dans la vue jalons/tâches, le survol d’un nom affiche son rôle. Seuls le PMO (les admins, dont l’accompagnant·e) et le Sponsor peuvent fermer un jalon.',
    'roles.stage.handover.label': 'Handover',
    'roles.stage.handover': 'Une fois tous les jalons fermés, le projet peut passer en Handover. Le Sponsor est responsable de valider ce passage.',
    'roles.stage.resources.label': 'Suivi des ressources',
    'roles.stage.resources': 'Dans Gérance → Ressources, le rôle et sa précision s’affichent à côté de chaque projet de la personne.',
    'roles.summary': 'Quatre rôles, du plus impliqué au moins impliqué : Accompagnant·e PMO, Sponsor, Contributeur·rice, Consulté·e. L’accompagnant·e PMO (choisi·e parmi les admins) et le Sponsor se définissent dans leurs champs de la fiche projet ; Contributeur·rice et Consulté·e se choisissent pour chaque personne de l’équipe dans l’onglet Cadrage. Le survol d’un rôle affiche son explication. Les noms et définitions sont provisoires.',
    'roles.colLevel': 'Niveau',
    'roles.colRole': 'Rôle',
    'roles.colDesc': 'Explication (affichée au survol)',

    // [Proposition: vue-jalons-taches]
    'vjt.title': 'Vue jalons / tâches',
    'vjt.summary': 'Voir sur une seule page les 4 niveaux d’un projet : jalons, tâches liées à chaque jalon, et la to-do list de chaque tâche — avec qui travaille sur quel jalon. Une tâche passe en Done à la main une fois sa to-do list entièrement cochée ; un jalon se ferme une fois toutes ses tâches Done ; le projet peut passer en Handover une fois tous ses jalons fermés.',
    'vjt.pickProject': 'Choisir un projet',
    'vjt.project.milestonesClosed': 'Jalons fermés',
    'vjt.project.lateMilestones': 'En retard : {names}',
    'vjt.project.handoverReady': 'Prêt pour le Handover',
    'vjt.project.handoverLocked': 'Handover possible quand tous les jalons sont fermés',
    'vjt.project.noMilestones': 'Ce projet n’a pas encore de jalons (onglet Planning du projet).',
    'vjt.ms.title': 'Jalon {n} · {name}',
    'vjt.ms.none': 'Tâches sans jalon',
    'vjt.ms.closed': 'Fermé',
    'vjt.ms.active': 'En cours',
    'vjt.ms.pending': 'À venir',
    'vjt.ms.close': 'Fermer le jalon',
    'vjt.ms.reopen': 'Rouvrir',
    'vjt.ms.closeHelp': 'Toutes les tâches sont Done : le jalon peut être fermé',
    'vjt.cancelMs.action': 'Annuler',
    'vjt.cancelMs.help': 'Annuler ce jalon : il reste à sa place, grisé, et ne compte plus ; la trace reste dans la chronologie et les commentaires',
    'vjt.cancelMs.title': 'Annuler le jalon « {name} » ?',
    'vjt.cancelMs.reason': 'Motif de l’annulation',
    'vjt.cancelMs.reasonPlaceholder': 'Pourquoi ce jalon n’est-il plus pertinent ?',
    'vjt.cancelMs.warning': 'Le jalon restera à sa place dans le planning, grisé, et ne comptera plus. L’annulation et son motif sont notés dans la chronologie et les commentaires.',
    'vjt.cancelMs.warningTasks': 'Le jalon restera à sa place dans le planning, grisé, et ne comptera plus ; ses {n} tâche(s) non terminée(s) passeront dans « Archivées ». L’annulation et son motif sont notés dans la chronologie et les commentaires.',
    'vjt.cancelMs.confirm': 'Annuler le jalon',
    'vjt.cancelled.badge': 'Annulé',
    'vjt.cancelled.by': 'Annulé le {date} par {name}',
    'vjt.cancelled.reason': 'motif : {reason}',
    'vjt.cancelled.archivedTasks': '{n} tâche(s) archivée(s)',
    'vjt.cancelled.restore': 'Restaurer',
    'vjt.cancelled.restoreHelp': 'Rouvrir ce jalon : il reprend son statut d’avant, ses tâches archivées reviennent dans leur colonne',
    'vjt.ms.closeLocked': '{n} tâche(s) pas encore Done',
    'vjt.ms.closeNotAllowed': 'Seuls le PMO (admins) ou le sponsor peuvent fermer un jalon',
    'vjt.ms.closedWithOpenTasks': 'Ce jalon est fermé mais contient {n} tâche(s) non terminée(s) — rouvrez-le.',
    'vjt.ms.people': 'Qui travaille sur ce jalon :',
    'vjt.ms.personOpenTasks': '{n} tâche(s) ouverte(s)',
    'vjt.ms.notOnTeam': 'hors équipe du projet',
    'vjt.ms.late': 'En retard',
    'vjt.ms.lateHelp': 'La fin prévue ({date}) est passée et le jalon n’est pas fermé',
    'vjt.ms.progressLabel': 'tâches terminées',
    'vjt.ms.details': 'Description et livrables du jalon',
    'vjt.ms.doneTasks': '{n} tâches terminées',
    'vjt.ms.doneTasksOne': '1 tâche terminée',
    'vjt.ms.show': 'afficher',
    'vjt.ms.hide': 'masquer',
    'vjt.ms.unfold': 'Déplier le jalon',
    'vjt.ms.fold': 'Replier le jalon',
    'vjt.task.addPlannedLabel': 'Date planifiée',
    'vjt.task.addDue': 'Échéance : {date} (fin du jalon)',
    'vjt.task.addNoDue': 'Ce jalon n’a pas de fin prévue : l’échéance ne peut pas en être déduite.',
    // [Proposition: collaborateurs-tache]
    // [Proposition: documents]
    'docs.title': 'Documents',
    'docs.titleTask': 'Documents de la tâche',
    'docs.titleMilestone': 'Documents du jalon',
    'docs.titleTodo': 'Documents de l’élément',
    'docs.titleProject': 'Documents généraux du projet',
    'docs.hintTask': 'Rattachés à la tâche entière. Pour un document propre à un élément de la to-do list, utilisez le trombone de cet élément.',
    'docs.hintMilestone': 'Rattachés au jalon entier. Pour un document propre à une tâche ou à un élément de la to-do list d’une tâche, ajoutez-le depuis cette tâche ou cet élément.',
    'docs.hintTodo': 'Rattachés à cet élément de la to-do list uniquement.',
    'docs.prop.title': 'Documents',
    // [Proposition: implications]
    'impl.title': 'Implications',
    'impl.summary': 'Quand quelqu’un vous attribue une tâche, vous ajoute comme collaborateur·rice d’une tâche, vous nomme sur un jalon ou vous donne un rôle dans un projet, vous recevez un e-mail (désactivable dans votre profil), sans notification dans l’application. Ces implications sont aussi listées dans Gérance → Implications : chacun y voit les siennes, le PMO voit tout le monde et en fait le point à la réunion du lundi. Les retards ne sont pas envoyés par e-mail : ils sont revus en séance.',
    'impl.where': 'Le même tableau est dans Gérance, onglet « Implications ».',
    // [Proposition: timeline-taches]
    'tlt.title': 'Timeline des tâches',
    'tlt.summary': 'Remplace « Planification des tâches » : les tâches d’un projet sur une grille d’une colonne par semaine. Chaque tâche est une bande dans la couleur de son statut, de sa création à son échéance, avec ses dates écrites dedans (planifiée, échéance, Done) et les dates repoussées en gris. Choisir une personne grise les tâches des autres. Un bouton orange signale une tâche modifiée depuis la dernière validation d’un admin : il ouvre à droite le journal des changements, qu’un admin valide en séance (les changements faits par un admin n’ont pas à être validés). Une tâche ne peut plus être créée sans date planifiée ni échéance.',
    'tlt.where': 'La même vue s’ouvre depuis le menu Tâches → Planification des tâches.',
    'tlt.everyone': 'Toutes les personnes',
    'tlt.onlyChanged': 'Seulement les tâches modifiées ({n})',
    'tlt.taskColumn': 'Tâche',
    'tlt.weekShort': 'S{n}',
    'tlt.milestone': 'Jalon',
    'tlt.tasksWithout': 'Sans jalon',
    'tlt.unnamedMilestone': 'Jalon sans nom',
    'tlt.milestoneEnd': 'Fin prévue du jalon : {date}',
    'tlt.milestoneEndShort': 'Fin prévue · {date}',
    'tlt.missingDates': 'Échéance à compléter',
    'tlt.missingDatesHelp': 'Tâche créée avant que les dates soient obligatoires : ajoutez-lui une échéance et une date planifiée.',
    'tlt.req.planned': 'La date planifiée est obligatoire pour créer une tâche.',
    'tlt.req.due': 'L’échéance est obligatoire pour créer une tâche (ou un jalon avec une date de fin prévue).',
    'tlt.req.dates': 'La date planifiée et l’échéance sont obligatoires.',
    'tlt.req.dueBeforeCreation': 'L’échéance ne peut pas être antérieure à la date de création de la tâche ({date}).',
    'tlt.req.dueBeforeCreationMilestone': 'L’échéance vient de la fin prévue du jalon, qui est antérieure à la date de création de la tâche ({date}). Demandez au PMO de mettre à jour la fin du jalon.',
    'tlt.req.milestoneNoEnd': 'Ce jalon n’a pas de date de fin prévue : l’échéance ne peut pas en être déduite. Demandez au PMO de la renseigner.',
    'tlt.emptyTitle': 'Aucune tâche',
    'tlt.emptyDesc': 'Aucune tâche ne correspond aux filtres choisis.',
    'tlt.openLog': 'Voir le journal des changements',
    'tlt.changes': '{n} changements',
    'tlt.changesOne': '1 changement',
    'tlt.changesHelp': 'Modifiée depuis la dernière validation d’un admin — ouvrir le journal',
    'tlt.movedCount': 'repoussée ×{n}',
    'tlt.movedHelp': 'Nombre de fois où la date planifiée ou l’échéance a été repoussée',
    'tlt.postponed': 'repoussée',
    'tlt.legend.planned': 'Planifiée',
    'tlt.legend.due': 'Échéance',
    'tlt.legend.done': 'Done',
    'tlt.legend.blocked': 'Bloquée',
    'tlt.legend.unblocked': 'Débloquée',
    'tlt.legend.previous': 'Date d’avant (repoussée)',
    'tlt.legend.msEnd': 'Fin prévue du jalon',
    'tlt.status.todo': 'To Do',
    'tlt.status.progress': 'In Progress',
    'tlt.status.blocked': 'Blocked',
    'tlt.status.done': 'Done',
    'tlt.mark.planned': 'Planifiée le {date}',
    'tlt.mark.due': 'Échéance le {date}',
    'tlt.mark.dueLate': 'Échéance le {date} — dépassée',
    'tlt.mark.done': 'Done le {date}',
    'tlt.mark.blocked': 'Bloquée le {date}',
    'tlt.mark.unblocked': 'Débloquée le {date}\nBloquée depuis le {since} ({n} j)',
    'tlt.mark.blockedReason': 'Bloquée le {date}\nRaison : {reason}',
    'tlt.mark.moved': 'Date initialement prévue : {date}\nChangée le {when} par {who}\nNouvelle date prévue : {next}',
    'tlt.mark.band': '{status} · créée le {date}',
    'tlt.log.kicker': 'Journal des changements',
    'tlt.log.pendingTitle': 'À valider ({n})',
    'tlt.log.validatedTitle': 'Déjà validés',
    'tlt.log.validateAll': 'Tout valider',
    'tlt.log.validateOne': 'Valider',
    'tlt.log.nothingPending': 'Aucun changement depuis la dernière validation.',
    'tlt.log.pmOnly': 'Seul le PMO (les admins) peut les valider.',
    'tlt.log.validatedBy': 'validé par {who} le {date}',
    'tlt.log.takenOver': 'repris de l’historique',
    'tlt.log.none': '(aucune)',
    'tlt.log.private': 'privée',
    'tlt.log.public': 'publique',
    'tlt.log.created': 'Tâche créée',
    'tlt.log.title': 'Titre',
    'tlt.log.description': 'Description modifiée',
    'tlt.log.status': 'Statut',
    'tlt.log.blocked_reason': 'Raison du blocage',
    'tlt.log.due_date': 'Échéance',
    'tlt.log.planned_at': 'Date planifiée',
    'tlt.log.assignee': 'Responsable',
    'tlt.log.privacy': 'Visibilité',
    'tlt.log.collaboratorAdded': 'Collaborateur·rice ajouté·e : {name}',
    'tlt.log.collaboratorRemoved': 'Collaborateur·rice retiré·e : {name}',
    'docs.prop.summary': 'Joindre des fichiers ou des liens vers un drive externe directement aux éléments de to-do list, aux tâches, aux jalons et au projet, en sachant toujours à quoi ils sont rattachés, qui les a ajoutés et quand.',
    'docs.prop.whereTitle': 'Où joindre un document',
    'docs.prop.where.todo': 'Élément de to-do list : le trombone à côté de l’élément (avec le nombre de documents).',
    'docs.prop.where.task': 'Tâche : section « Documents » de la page de la tâche, de sa fenêtre et du panneau d’information.',
    'docs.prop.where.milestone': 'Jalon : section « Documents » en bas du jalon déplié, dans la vue jalons/tâches.',
    'docs.prop.where.project': 'Projet : « Documents généraux du projet », en haut de « Documents du projet » (charte, contrat…).',
    'docs.prop.traceTitle': 'Comment c’est documenté',
    'docs.prop.trace.kinds': 'Fichier (envoyé dans PMO) ou lien vers un document gardé sur SharePoint, OneDrive, Google Drive…',
    'docs.prop.trace.who': 'Chaque document indique qui l’a ajouté et quand ; seule cette personne ou le PMO peut le retirer.',
    'docs.prop.trace.register': '« Documents du projet », en bas de la vue jalons/tâches : tous les documents du projet avec ce à quoi ils sont rattachés (jalon › tâche › élément), filtrables par jalon et par type. Les fichiers joints aux commentaires y figurent aussi.',
    'docs.prop.trace.attach': '« Rattacher à… » : tout document, y compris un ancien fichier joint à un commentaire, peut être rangé sur un élément, une tâche, un jalon ou le projet. Il reste visible dans son commentaire.',
    'docs.prop.trace.history': 'Chaque ajout, retrait et rattachement est inscrit dans l’historique du projet.',
    'docs.prop.trace.private': 'Un document d’une tâche privée suit la tâche : seules les personnes qui peuvent l’ouvrir le voient.',
    'docs.prop.trace.next': 'Étape suivante : versions des fichiers et historique des liens.',
    'docs.empty': 'Aucun document.',
    'docs.addFile': 'Joindre un fichier',
    'docs.addLink': 'Ajouter un lien',
    'docs.linkUrlPlaceholder': 'Adresse du document (SharePoint, OneDrive, Google Drive…)',
    'docs.linkNamePlaceholder': 'Nom du document (facultatif)',
    'docs.linkSave': 'Ajouter',
    'docs.badUrl': 'Indiquez une adresse complète, commençant par https://',
    'docs.kindFile': 'Fichier',
    'docs.kindLink': 'Lien',
    'docs.addedBy': 'ajouté par {who} le {date}',
    'docs.remove': 'Retirer le document',
    'docs.removeConfirm': 'Retirer « {name} » ? Le retrait est inscrit dans l’historique du projet.',
    'docs.addConfirmTitle': 'Joindre au jalon',
    'docs.addConfirm': 'Joindre « {name} » à « {target} » ?',
    'docs.addConfirmAction': 'Joindre',
    'docs.todoToggle': 'Documents de cet élément',
    'docs.taskCount': 'Documents liés à cette tâche : {n}',
    'docs.linkedTask': 'Tâche : {title}',
    'docs.registerTitle': 'Documents du projet',
    'docs.registerEmpty': 'Aucun document pour ce filtre.',
    'docs.filterAllMilestones': 'Tous les jalons',
    'docs.filterAllKinds': 'Fichiers et liens',
    'docs.colName': 'Nom',
    'docs.colKind': 'Type',
    'docs.colAttachedTo': 'Rattaché à',
    'docs.colAddedBy': 'Ajouté par',
    'docs.colAddedAt': 'Le',
    'docs.at.todo': 'élément de to-do list',
    'docs.at.task': 'tâche',
    'docs.at.milestone': 'jalon',
    'docs.msCancelled': 'Jalon « {name} » (annulé)',
    'docs.msCancelledN': 'Jalon {n} · {name} (annulé)',
    'docs.msDeleted': 'Jalon supprimé',
    'docs.at.task_comment': 'commentaire de tâche',
    'docs.at.project_comment': 'commentaire du projet',
    'docs.at.meeting': 'réunion',
    'docs.at.transcription': 'transcription',
    'docs.at.project': 'document général du projet',
    'docs.eventFileAdded': 'Fichier « {name} » ajouté à « {target} »',
    'docs.eventLinkAdded': 'Lien « {name} » ajouté à « {target} »',
    'docs.eventRemoved': 'Document « {name} » retiré de « {target} »',
    'docs.eventAttached': 'Document « {name} » rattaché à « {target} »',
    'docs.summaryLatest': 'Les {n} plus récents',
    'docs.summaryShowAll': 'Voir tous les documents',
    'docs.summaryShowLess': 'Afficher moins',
    'docs.summaryGoToRegister': 'Aller au registre',
    'docs.generalTitle': 'Documents généraux du projet (charte, contrat…)',
    'docs.target.project': 'Projet (document général)',
    'docs.attachAction': 'Rattacher à…',
    'docs.attachPlaceholder': 'Choisir le jalon, la tâche ou l’élément',
    'docs.attachConfirmTitle': 'Rattacher le document',
    'docs.attachConfirm': 'Rattacher « {name} » à « {to} » ? Il est actuellement rattaché à « {from} ». Le changement est inscrit dans l’historique du projet.',
    'docs.attachConfirmAction': 'Rattacher',
    'docs.fromComment': 'Joint à l’origine à un commentaire, toujours visible dans ce commentaire',
    'docs.commentHint': 'Un document de travail se joint plutôt à l’élément de to-do list, à la tâche ou au jalon concernés.',
    'collab.title': 'Collaborateurs d’une tâche',
    'collab.summary': 'Une tâche a un·e responsable (la personne assignée) et peut avoir plusieurs collaborateur·rice·s. Tous se choisissent parmi les personnes impliquées dans le projet, ajoutées en amont dans l’onglet Aperçu de la fiche projet.',
    'collab.label': 'Collaborateur·rice·s',
    'collab.placeholder': 'Ajouter des collaborateur·rice·s',
    'collab.none': 'Aucun·e',
    'collab.notOnProject': 'plus dans le projet',
    'collab.hint': 'Le ou la responsable et les collaborateur·rice·s se choisissent parmi les personnes impliquées dans le projet. Une personne n’apparaît pas ? Elle doit d’abord être ajoutée au projet, dans l’onglet Aperçu de la fiche projet (Collaborateurs).',
    'collab.hintLink': 'Ouvrir la fiche projet',
    'collab.cardTitle': 'Collaborateur·rice·s : {names}',
    'collab.whatTitle': 'Ce qui change',
    'collab.what.field': 'Un champ « Collaborateur·rice·s » à côté du ou de la responsable : à la création d’une tâche, dans la page d’une tâche et dans sa fenêtre.',
    'collab.what.reminder': 'Une icône ⓘ à côté du champ rappelle, au survol, que seules les personnes du projet apparaissent, et qu’une personne manquante s’ajoute d’abord dans l’onglet Aperçu de la fiche projet.',
    'collab.what.card': 'Sur la carte de la tâche, « +n » après le nom du ou de la responsable ; le survol nomme les collaborateur·rice·s.',
    'collab.what.private': 'Une tâche privée peut être ouverte par ses collaborateur·rice·s, avec ses documents et sa to-do list. Seul·e·s le créateur ou la créatrice, le ou la responsable et le PMO peuvent changer sa confidentialité.',
    'collab.what.notify': 'La personne ajoutée est prévenue par e-mail, et l’ajout apparaît dans Gérance → Implications.',
    'collab.what.mine': 'La tâche apparaît dans leurs propres tâches (Mes tâches, planificateur, échéances) et ils figurent dans « Qui travaille sur ce jalon ».',
    'collab.stepTitle': 'Étape du projet où l’on ajoute les personnes',
    'collab.step': 'Les personnes impliquées se définissent dans l’onglet Aperçu de la fiche projet : le ou la sponsor et les collaborateurs. Elles reçoivent ensuite leur rôle dans l’onglet Cadrage. Ce n’est qu’après qu’elles peuvent être choisies sur une tâche.',
    'vjt.ms.noTasks': 'Aucune tâche liée à ce jalon.',
    'vjt.ms.descriptionLabel': 'Description du jalon :',
    'vjt.prompt.task': 'Tous les éléments de la to-do list sont cochés. Souhaitez-vous valider la tâche ?',
    'vjt.prompt.taskAction': 'Valider la tâche',
    'vjt.prompt.milestone': 'Toutes les tâches du jalon sont terminées. Souhaitez-vous fermer le jalon ?',
    'vjt.prompt.milestoneAction': 'Fermer le jalon',
    'vjt.prompt.project': 'Tous les jalons sont terminés. Souhaitez-vous livrer le projet ?',
    'vjt.prompt.projectAction': 'Livrer le projet',
    'vjt.prompt.projectConfirmTitle': 'Livrer le projet ?',
    'vjt.prompt.projectConfirmMessage': 'Le projet « {name} » passera à l’étape Handover (livraison). Le changement est inscrit dans l’historique du projet.',
    'vjt.ms.deliverables': 'Livrables liés :',
    'vjt.task.open': 'Ouvrir la page de la tâche',
    'vjt.task.openHere': 'Ouvrir la tâche',
    'vjt.task.unassigned': 'Non assignée',
    'vjt.task.markDone': 'Marquer Done',
    'vjt.task.doneHelp': 'Passer la tâche en Done',
    'vjt.task.statusHelp': 'Statut de la tâche : passez-la en In Progress quand le travail commence (par ex. en créant sa to-do list). Done se fait avec « Marquer Done ».',
    'vjt.task.doneLocked': '{n} élément(s) de la to-do list à cocher',
    'vjt.task.doneWithOpenTodos': 'Tâche Done alors que {n} élément(s) de sa to-do list ne sont pas cochés',
    'vjt.task.reopen': 'Rouvrir',
    'vjt.task.add': 'Ajouter',
    'vjt.task.addPlaceholder': 'Nouvelle tâche…',
    'vjt.todo.toggle': 'Afficher / masquer la to-do list',
    'vjt.todo.header': 'To-do list',
    'vjt.todo.count': 'To-do {done}/{total}',
    'vjt.todo.addFirst': '+ To-do',
    'vjt.todo.add': 'Ajouter',
    'vjt.todo.plannedDate': 'Date planifiée',
    'vjt.todo.dueDate': 'Échéance',
    'vjt.todo.overdue': 'Échéance dépassée',
    'vjt.todo.plannedClamped': 'La date planifiée d’un élément ne peut pas dépasser l’échéance de sa tâche : elle a été ramenée au {date}.',
    'vjt.todo.dueClamped': 'L’échéance d’un élément ne peut pas dépasser celle de sa tâche : elle a été ramenée au {date}.',
    'vjt.todo.setDates': 'Ajouter une date planifiée / une échéance',
    'vjt.todo.rename': 'Renommer l’élément',
    'vjt.todo.showDescription': 'Voir la description et le suivi',
    'vjt.todo.hideDescription': 'Masquer la description et le suivi',
    'vjt.todo.addDescription': 'Ajouter une description / un suivi',
    'vjt.todo.commentsToggle': 'Commentaires de cet élément',
    'vjt.todo.commentsHint': 'Échanges sur cet élément. Tapez @ pour mentionner une personne : elle est prévenue dans l’application et par e-mail.',
    'vjt.todo.editDescription': 'Modifier la description et le suivi',
    'vjt.todo.descriptionPlaceholder': 'Ce qu’il faut faire, où on en est…',
    'vjt.todo.historyLast': 'Mis à jour par {who} le {date}',
    'vjt.legacyNotesHint': 'Ancien champ, en lecture seule : reportez cette note dans la « Description et suivi » de l’élément de to-do list concerné.',
    'vjt.todo.historyToggle': 'Versions précédentes ({n})',
    'vjt.todo.historyBy': '{who}, le {date}',
    'vjt.todo.historyInitial': 'Version initiale',
    'vjt.todo.historyEmpty': '(texte effacé)',
    'vjt.todo.reopenTitle': 'Rouvrir la tâche ?',
    'vjt.todo.reopenMessage': 'Cette tâche est Done. Ajouter un élément à sa to-do list va la rouvrir (retour en In Progress).',
    'vjt.todo.reopenConfirm': 'Ajouter et rouvrir',
    'vjt.task.doneRefused': 'Impossible de passer cette tâche en Done : sa to-do list n’est pas entièrement cochée.',
    'vjt.todo.empty': 'Pas de to-do list — la tâche peut passer en Done directement.',
    'vjt.todo.addPlaceholder': 'Ajouter un élément à la to-do list…',
    'vjt.todo.checkedBy': 'Coché par {who} le {date}',
    'vjt.reopenConfirm.title': 'Rouvrir le jalon ?',
    'vjt.reopenConfirm.message': 'Le jalon « {name} » est fermé. Y ajouter une tâche va le rouvrir.',
    'vjt.reopenConfirm.confirm': 'Ajouter et rouvrir',
  },
  en: {
    'page.proposals': 'Proposal: new milestones/tasks view',
    'shell.nav.proposals': 'Proposal: new milestones/tasks view',
    'proposals.intro': 'Proposed changes to the milestones/tasks view, submitted to the PMO. Visible to authorized people only.',
    'proposals.emptyTitle': 'No proposals yet',
    'proposals.emptyDesc': 'Proposals will appear here as they are added, each in its own tab.',

    // [Proposition: roles-projet]
    'roles.accompanist': 'PMO accompanist',
    'roles.accompanist.desc': 'Runs the project day to day with the team: organises milestones, assigns tasks, tracks progress. Chosen among the admins (the PMO), who alone validate changes.',
    'roles.sponsor': 'Sponsor',
    'roles.sponsor.desc': 'Owns the project and answers for it. Arbitrates, approves the major steps and the move to Handover.',
    'roles.contributor': 'Contributor',
    'roles.contributor.desc': 'Carries out project tasks and reports on them.',
    'roles.consulted': 'Consulted',
    'roles.consulted.desc': 'Asked occasionally for their opinion or expertise on a specific task, without being responsible for it.',
    'roles.sponsorFromField': 'The sponsor and the PMO accompanist are set only in the project’s “Sponsor” and “PMO Accompanist” fields.',
    'roles.none': 'To be defined',
    'roles.none.desc': 'No role chosen for this person.',
    'roles.header': 'Role',
    'roles.precision': 'Details',
    'roles.precisionPlaceholder': 'Details (e.g. supplier negotiation)',
    'roles.title': 'Project roles',
    'roles.teamTitle': 'Project team and roles',
    'roles.teamOpen': 'Team and roles',
    'roles.teamOpenHelp': 'See who does what on the project: each person, their role and its details',
    'roles.teamEmpty': 'Nobody is on this project’s team yet (Scoping tab).',
    'roles.standardDesc': 'Standard role',
    'roles.projectPrecision': 'Details for this project',
    'roles.definitionsTitle': 'The 4 roles',
    'roles.stagesTitle': 'Roles across the project stages',
    'roles.stage.cadrage.label': 'Scoping',
    'roles.stage.cadrage': 'The team is put together: each person gets a role and, if needed, details. The Sponsor is the person named in the project’s “Sponsor” field. Each person added is told by e-mail and finds that role in Gérance → Involvements.',
    'roles.stage.planning.label': 'Planning',
    'roles.stage.planning': 'The PMO accompanist organises the milestones and assigns tasks to team members.',
    'roles.stage.execution.label': 'Execution',
    'roles.stage.execution': 'Contributors carry out the tasks; Consulted people are asked when needed. In the milestones/tasks view, hovering a name shows their role. Only the PMO (the admins, including the accompanist) and the Sponsor can close a milestone.',
    'roles.stage.handover.label': 'Handover',
    'roles.stage.handover': 'Once every milestone is closed, the project can move to Handover. The Sponsor is responsible for approving this move.',
    'roles.stage.resources.label': 'Resource tracking',
    'roles.stage.resources': 'In Management → Resources, the role and its details show next to each of the person’s projects.',
    'roles.summary': 'Four roles, from the most to the least involved: PMO accompanist, Sponsor, Contributor, Consulted. The PMO accompanist (chosen among the admins) and the Sponsor are set in their own project fields; Contributor and Consulted are chosen for each team member on the Scoping tab. Hovering a role shows its explanation. Names and definitions are provisional.',
    'roles.colLevel': 'Level',
    'roles.colRole': 'Role',
    'roles.colDesc': 'Explanation (shown on hover)',

    // [Proposition: vue-jalons-taches]
    'vjt.title': 'Milestones / tasks view',
    'vjt.summary': 'See a project’s 4 levels on one page: milestones, the tasks linked to each milestone, and each task’s to-do list — with who works on which milestone. A task is marked Done by hand once its to-do list is fully checked; a milestone closes once all its tasks are Done; the project can go to Handover once every milestone is closed.',
    'vjt.pickProject': 'Pick a project',
    'vjt.project.milestonesClosed': 'Milestones closed',
    'vjt.project.lateMilestones': 'Overdue: {names}',
    'vjt.project.handoverReady': 'Ready for Handover',
    'vjt.project.handoverLocked': 'Handover possible once every milestone is closed',
    'vjt.project.noMilestones': 'This project has no milestones yet (project Planning tab).',
    'vjt.ms.title': 'Milestone {n} · {name}',
    'vjt.ms.none': 'Tasks without a milestone',
    'vjt.ms.closed': 'Closed',
    'vjt.ms.active': 'In progress',
    'vjt.ms.pending': 'Upcoming',
    'vjt.ms.close': 'Close milestone',
    'vjt.ms.reopen': 'Reopen',
    'vjt.ms.closeHelp': 'All tasks are Done: the milestone can be closed',
    'vjt.cancelMs.action': 'Cancel',
    'vjt.cancelMs.help': 'Cancel this milestone: it stays in its place, greyed, and no longer counts; the trace stays in the timeline and comments',
    'vjt.cancelMs.title': 'Cancel milestone “{name}”?',
    'vjt.cancelMs.reason': 'Reason for cancelling',
    'vjt.cancelMs.reasonPlaceholder': 'Why is this milestone no longer relevant?',
    'vjt.cancelMs.warning': 'The milestone will stay in its place in the planning, greyed, and no longer count. The cancellation and its reason are noted in the timeline and comments.',
    'vjt.cancelMs.warningTasks': 'The milestone will stay in its place in the planning, greyed, and no longer count; its {n} unfinished task(s) will move to “Archived”. The cancellation and its reason are noted in the timeline and comments.',
    'vjt.cancelMs.confirm': 'Cancel milestone',
    'vjt.cancelled.badge': 'Cancelled',
    'vjt.cancelled.by': 'Cancelled on {date} by {name}',
    'vjt.cancelled.reason': 'reason: {reason}',
    'vjt.cancelled.archivedTasks': '{n} archived task(s)',
    'vjt.cancelled.restore': 'Restore',
    'vjt.cancelled.restoreHelp': 'Reopen this milestone: it gets its former status back, its archived tasks return to their column',
    'vjt.ms.closeLocked': '{n} task(s) not Done yet',
    'vjt.ms.closeNotAllowed': 'Only the PMO (admins) or the sponsor can close a milestone',
    'vjt.ms.closedWithOpenTasks': 'This milestone is closed but has {n} unfinished task(s) — reopen it.',
    'vjt.ms.people': 'Working on this milestone:',
    'vjt.ms.personOpenTasks': '{n} open task(s)',
    'vjt.ms.notOnTeam': 'not on the project team',
    'vjt.ms.late': 'Late',
    'vjt.ms.lateHelp': 'The planned end ({date}) has passed and the milestone isn’t closed',
    'vjt.ms.progressLabel': 'tasks done',
    'vjt.ms.details': 'Milestone description and deliverables',
    'vjt.ms.doneTasks': '{n} tasks done',
    'vjt.ms.doneTasksOne': '1 task done',
    'vjt.ms.show': 'show',
    'vjt.ms.hide': 'hide',
    'vjt.ms.unfold': 'Unfold the milestone',
    'vjt.ms.fold': 'Fold the milestone',
    'vjt.task.addPlannedLabel': 'Planned date',
    'vjt.task.addDue': 'Due: {date} (milestone end)',
    'vjt.task.addNoDue': 'This milestone has no planned end, so no due date can be derived from it.',
    // [Proposition: collaborateurs-tache]
    // [Proposition: documents]
    'docs.title': 'Documents',
    'docs.titleTask': 'Task documents',
    'docs.titleMilestone': 'Milestone documents',
    'docs.titleTodo': 'Item documents',
    'docs.titleProject': 'General project documents',
    'docs.hintTask': 'Attached to the whole task. For a document about one to-do item, use that item’s paperclip.',
    'docs.hintMilestone': 'Attached to the whole milestone. For a document about one task or one of its to-do items, add it from that task or item.',
    'docs.hintTodo': 'Attached to this to-do item only.',
    'docs.prop.title': 'Documents',
    // [Proposition: implications]
    'impl.title': 'Involvements',
    'impl.summary': 'When someone assigns you a task, adds you as a task collaborator, names you on a milestone or gives you a role in a project, you get an e-mail (can be turned off in your profile), with no in-app notification. These involvements are also listed in Gérance → Involvements: everyone sees their own, the PMO sees everyone and reviews them in the Monday meeting. Delays aren’t e-mailed: they are reviewed in the meetings.',
    'impl.where': 'The same table is in Gérance, “Involvements” tab.',
    // [Proposition: timeline-taches]
    'tlt.title': 'Task timeline',
    'tlt.summary': 'Replaces “Task planning”: a project’s tasks on a grid with one column per week. Each task is a band in its status colour, from its creation to its due date, with its dates written inside (planned, due, Done) and pushed-back dates in grey. Picking a person greys out everyone else’s tasks. An orange button flags a task changed since an admin last validated it: it opens the change log on the right, which an admin validates in the meeting (changes made by an admin need no validation). A task can no longer be created without a planned date and a due date.',
    'tlt.where': 'The same view opens from Tasks → Task planning.',
    'tlt.everyone': 'Everyone',
    'tlt.onlyChanged': 'Changed tasks only ({n})',
    'tlt.taskColumn': 'Task',
    'tlt.weekShort': 'W{n}',
    'tlt.milestone': 'Milestone',
    'tlt.tasksWithout': 'No milestone',
    'tlt.unnamedMilestone': 'Unnamed milestone',
    'tlt.milestoneEnd': 'Milestone planned end: {date}',
    'tlt.milestoneEndShort': 'Planned end · {date}',
    'tlt.missingDates': 'Due date missing',
    'tlt.missingDatesHelp': 'Task created before dates were required: give it a due date and a planned date.',
    'tlt.req.planned': 'A planned date is required to create a task.',
    'tlt.req.due': 'A due date is required to create a task (or a milestone with a planned end date).',
    'tlt.req.dates': 'The planned date and the due date are required.',
    'tlt.req.dueBeforeCreation': 'The due date cannot be earlier than the task’s creation date ({date}).',
    'tlt.req.dueBeforeCreationMilestone': 'The due date comes from the milestone’s planned end, which is earlier than the task’s creation date ({date}). Ask the PMO to update the milestone’s end.',
    'tlt.req.milestoneNoEnd': 'This milestone has no planned end date, so no due date can be derived from it. Ask the PMO to fill it in.',
    'tlt.emptyTitle': 'No tasks',
    'tlt.emptyDesc': 'No task matches the selected filters.',
    'tlt.openLog': 'See the change log',
    'tlt.changes': '{n} changes',
    'tlt.changesOne': '1 change',
    'tlt.changesHelp': 'Changed since an admin last validated it — open the log',
    'tlt.movedCount': 'pushed back ×{n}',
    'tlt.movedHelp': 'Number of times the planned date or due date was pushed back',
    'tlt.postponed': 'pushed back',
    'tlt.legend.planned': 'Planned',
    'tlt.legend.due': 'Due',
    'tlt.legend.done': 'Done',
    'tlt.legend.blocked': 'Blocked',
    'tlt.legend.unblocked': 'Unblocked',
    'tlt.legend.previous': 'Earlier date (pushed back)',
    'tlt.legend.msEnd': 'Milestone planned end',
    'tlt.status.todo': 'To Do',
    'tlt.status.progress': 'In Progress',
    'tlt.status.blocked': 'Blocked',
    'tlt.status.done': 'Done',
    'tlt.mark.planned': 'Planned on {date}',
    'tlt.mark.due': 'Due on {date}',
    'tlt.mark.dueLate': 'Due on {date} — overdue',
    'tlt.mark.done': 'Done on {date}',
    'tlt.mark.blocked': 'Blocked on {date}',
    'tlt.mark.unblocked': 'Unblocked on {date}\nBlocked since {since} ({n} d)',
    'tlt.mark.blockedReason': 'Blocked on {date}\nReason: {reason}',
    'tlt.mark.moved': 'Initially planned for: {date}\nChanged on {when} by {who}\nNew date planned: {next}',
    'tlt.mark.band': '{status} · created on {date}',
    'tlt.log.kicker': 'Change log',
    'tlt.log.pendingTitle': 'To validate ({n})',
    'tlt.log.validatedTitle': 'Already validated',
    'tlt.log.validateAll': 'Validate all',
    'tlt.log.validateOne': 'Validate',
    'tlt.log.nothingPending': 'No change since the last validation.',
    'tlt.log.pmOnly': 'Only the PMO (the admins) can validate them.',
    'tlt.log.validatedBy': 'validated by {who} on {date}',
    'tlt.log.takenOver': 'taken over from history',
    'tlt.log.none': '(none)',
    'tlt.log.private': 'private',
    'tlt.log.public': 'public',
    'tlt.log.created': 'Task created',
    'tlt.log.title': 'Title',
    'tlt.log.description': 'Description changed',
    'tlt.log.status': 'Status',
    'tlt.log.blocked_reason': 'Blocked reason',
    'tlt.log.due_date': 'Due date',
    'tlt.log.planned_at': 'Planned date',
    'tlt.log.assignee': 'Assignee',
    'tlt.log.privacy': 'Visibility',
    'tlt.log.collaboratorAdded': 'Collaborator added: {name}',
    'tlt.log.collaboratorRemoved': 'Collaborator removed: {name}',
    'docs.prop.summary': 'Attach files, or links to an external drive, straight to to-do items, tasks, milestones and the project — always knowing what they are attached to, who added them and when.',
    'docs.prop.whereTitle': 'Where to attach a document',
    'docs.prop.where.todo': 'To-do item: the paperclip next to the item (with the number of documents).',
    'docs.prop.where.task': 'Task: “Documents” section of the task page, its window and the info panel.',
    'docs.prop.where.milestone': 'Milestone: “Documents” section at the bottom of the unfolded milestone, in the milestones/tasks view.',
    'docs.prop.where.project': 'Project: “General project documents”, at the top of “Project documents” (charter, contract…).',
    'docs.prop.traceTitle': 'How it is traced',
    'docs.prop.trace.kinds': 'A file (uploaded to PMO) or a link to a document kept on SharePoint, OneDrive, Google Drive…',
    'docs.prop.trace.who': 'Each document shows who added it and when; only that person or the PMO can remove it.',
    'docs.prop.trace.register': '“Project documents”, at the bottom of the milestones/tasks view: every document of the project with what it is attached to (milestone › task › item), filterable by milestone and type. Comment files are listed too.',
    'docs.prop.trace.attach': '“Attach to…”: any document, including an old file joined to a comment, can be filed on an item, a task, a milestone or the project. It stays visible in its comment.',
    'docs.prop.trace.history': 'Every addition, removal and re-attachment is recorded in the project history.',
    'docs.prop.trace.private': 'A private task’s document follows the task: only the people who can open it see it.',
    'docs.prop.trace.next': 'Next step: file versions and link history.',
    'docs.empty': 'No documents.',
    'docs.addFile': 'Attach a file',
    'docs.addLink': 'Add a link',
    'docs.linkUrlPlaceholder': 'Document address (SharePoint, OneDrive, Google Drive…)',
    'docs.linkNamePlaceholder': 'Document name (optional)',
    'docs.linkSave': 'Add',
    'docs.badUrl': 'Enter a full address, starting with https://',
    'docs.kindFile': 'File',
    'docs.kindLink': 'Link',
    'docs.addedBy': 'added by {who} on {date}',
    'docs.remove': 'Remove the document',
    'docs.removeConfirm': 'Remove “{name}”? The removal is recorded in the project history.',
    'docs.addConfirmTitle': 'Attach to the milestone',
    'docs.addConfirm': 'Attach “{name}” to “{target}”?',
    'docs.addConfirmAction': 'Attach',
    'docs.todoToggle': 'Documents of this item',
    'docs.taskCount': 'Documents linked to this task: {n}',
    'docs.linkedTask': 'Task: {title}',
    'docs.registerTitle': 'Project documents',
    'docs.registerEmpty': 'No documents for this filter.',
    'docs.filterAllMilestones': 'All milestones',
    'docs.filterAllKinds': 'Files and links',
    'docs.colName': 'Name',
    'docs.colKind': 'Type',
    'docs.colAttachedTo': 'Attached to',
    'docs.colAddedBy': 'Added by',
    'docs.colAddedAt': 'On',
    'docs.at.todo': 'to-do item',
    'docs.at.task': 'task',
    'docs.at.milestone': 'milestone',
    'docs.msCancelled': 'Milestone "{name}" (cancelled)',
    'docs.msCancelledN': 'Milestone {n} · {name} (cancelled)',
    'docs.msDeleted': 'Deleted milestone',
    'docs.at.task_comment': 'task comment',
    'docs.at.project_comment': 'project comment',
    'docs.at.meeting': 'meeting',
    'docs.at.transcription': 'transcript',
    'docs.at.project': 'general project document',
    'docs.eventFileAdded': 'File “{name}” added to “{target}”',
    'docs.eventLinkAdded': 'Link “{name}” added to “{target}”',
    'docs.eventRemoved': 'Document “{name}” removed from “{target}”',
    'docs.eventAttached': 'Document “{name}” attached to “{target}”',
    'docs.summaryLatest': 'The {n} most recent',
    'docs.summaryShowAll': 'See all documents',
    'docs.summaryShowLess': 'Show less',
    'docs.summaryGoToRegister': 'Go to the register',
    'docs.generalTitle': 'General project documents (charter, contract…)',
    'docs.target.project': 'Project (general document)',
    'docs.attachAction': 'Attach to…',
    'docs.attachPlaceholder': 'Choose the milestone, task or item',
    'docs.attachConfirmTitle': 'Re-attach the document',
    'docs.attachConfirm': 'Attach “{name}” to “{to}”? It is currently attached to “{from}”. The change is recorded in the project history.',
    'docs.attachConfirmAction': 'Attach',
    'docs.fromComment': 'Originally joined to a comment, still shown in that comment',
    'docs.commentHint': 'A working document is better attached to the to-do item, task or milestone it concerns.',
    'collab.title': 'Task collaborators',
    'collab.summary': 'A task has one assignee and can have several collaborators. All are picked among the people involved in the project, added upstream on the project’s Overview tab.',
    'collab.label': 'Collaborators',
    'collab.placeholder': 'Add collaborators',
    'collab.none': 'None',
    'collab.notOnProject': 'no longer on the project',
    'collab.hint': 'The assignee and collaborators are picked among the people involved in the project. Someone missing? Add them to the project first, on the project’s Overview tab (Collaborators).',
    'collab.hintLink': 'Open the project',
    'collab.cardTitle': 'Collaborators: {names}',
    'collab.whatTitle': 'What changes',
    'collab.what.field': 'A “Collaborators” field next to the assignee: when creating a task, on the task page and in the task window.',
    'collab.what.reminder': 'An ⓘ next to the field says, on hover, that only the project’s people are listed, and that someone missing must first be added on the project’s Overview tab.',
    'collab.what.card': 'On the task card, “+n” after the assignee’s name; hovering names the collaborators.',
    'collab.what.private': 'A private task can be opened by its collaborators, with its documents and to-do list. Only its creator, its assignee and the PMO can change its privacy.',
    'collab.what.notify': 'The person added gets an e-mail, and the addition shows in Gérance → Involvements.',
    'collab.what.mine': 'The task shows among their own tasks (My tasks, planner, deadlines) and they appear under “Working on this milestone”.',
    'collab.stepTitle': 'Project stage where people are added',
    'collab.step': 'The people involved are set on the project’s Overview tab: the sponsor and the collaborators. They then get their role on the Scoping tab. Only after that can they be picked on a task.',
    'vjt.ms.noTasks': 'No task linked to this milestone.',
    'vjt.ms.descriptionLabel': 'Milestone description:',
    'vjt.prompt.task': 'Every to-do item is checked. Do you want to validate the task?',
    'vjt.prompt.taskAction': 'Validate the task',
    'vjt.prompt.milestone': 'Every task of the milestone is done. Do you want to close the milestone?',
    'vjt.prompt.milestoneAction': 'Close the milestone',
    'vjt.prompt.project': 'Every milestone is closed. Do you want to deliver the project?',
    'vjt.prompt.projectAction': 'Deliver the project',
    'vjt.prompt.projectConfirmTitle': 'Deliver the project?',
    'vjt.prompt.projectConfirmMessage': 'Project “{name}” will move to the Handover stage (delivery). The change is recorded in the project history.',
    'vjt.ms.deliverables': 'Linked deliverables:',
    'vjt.task.open': 'Open the task page',
    'vjt.task.openHere': 'Open the task',
    'vjt.task.unassigned': 'Unassigned',
    'vjt.task.markDone': 'Mark Done',
    'vjt.task.doneHelp': 'Move the task to Done',
    'vjt.task.statusHelp': 'Task status: move it to In Progress when work starts (e.g. when building its to-do list). Done goes through “Mark Done”.',
    'vjt.task.doneLocked': '{n} to-do item(s) left to check',
    'vjt.task.doneWithOpenTodos': 'Task is Done but {n} item(s) of its to-do list are unchecked',
    'vjt.task.reopen': 'Reopen',
    'vjt.task.add': 'Add',
    'vjt.task.addPlaceholder': 'New task…',
    'vjt.todo.toggle': 'Show / hide the to-do list',
    'vjt.todo.header': 'To-do list',
    'vjt.todo.count': 'To-do {done}/{total}',
    'vjt.todo.addFirst': '+ To-do',
    'vjt.todo.add': 'Add',
    'vjt.todo.plannedDate': 'Planned date',
    'vjt.todo.dueDate': 'Due date',
    'vjt.todo.overdue': 'Overdue',
    'vjt.todo.plannedClamped': 'An item can’t be planned after its task’s due date: the planned date was pulled back to {date}.',
    'vjt.todo.dueClamped': 'An item can’t be due after its task: the due date was pulled back to {date}.',
    'vjt.todo.setDates': 'Add a planned date / due date',
    'vjt.todo.rename': 'Rename the item',
    'vjt.todo.showDescription': 'Show the description and follow-up',
    'vjt.todo.hideDescription': 'Hide the description and follow-up',
    'vjt.todo.addDescription': 'Add a description / follow-up',
    'vjt.todo.commentsToggle': 'Comments on this item',
    'vjt.todo.commentsHint': 'Discussion about this item. Type @ to mention someone: they are notified in the app and by e-mail.',
    'vjt.todo.editDescription': 'Edit the description and follow-up',
    'vjt.todo.descriptionPlaceholder': 'What needs doing, where it stands…',
    'vjt.todo.historyLast': 'Updated by {who} on {date}',
    'vjt.legacyNotesHint': 'Former field, read-only: move this note into the “Description and follow-up” of the to-do item it concerns.',
    'vjt.todo.historyToggle': 'Earlier versions ({n})',
    'vjt.todo.historyBy': '{who}, on {date}',
    'vjt.todo.historyInitial': 'First version',
    'vjt.todo.historyEmpty': '(text cleared)',
    'vjt.todo.reopenTitle': 'Reopen the task?',
    'vjt.todo.reopenMessage': 'This task is Done. Adding an item to its to-do list will reopen it (back to In Progress).',
    'vjt.todo.reopenConfirm': 'Add and reopen',
    'vjt.task.doneRefused': 'This task can’t go to Done: its to-do list isn’t fully checked.',
    'vjt.todo.empty': 'No to-do list — the task can go to Done directly.',
    'vjt.todo.addPlaceholder': 'Add a to-do item…',
    'vjt.todo.checkedBy': 'Checked by {who} on {date}',
    'vjt.reopenConfirm.title': 'Reopen the milestone?',
    'vjt.reopenConfirm.message': 'Milestone “{name}” is closed. Adding a task to it will reopen it.',
    'vjt.reopenConfirm.confirm': 'Add and reopen',
  },
};
